import React, { useState, useEffect, Suspense } from 'react'
import { Box, Menu, MenuItem } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useSelector } from 'react-redux'
import { format, parseISO } from 'date-fns'
import { toast } from 'react-hot-toast'

import PipelineHeaderActions from 'src/components/dealPipeline/PipelineHeaderActions'
import PipelineSelector from 'src/components/dealPipeline/PipelineSelector'
import {
  useGetAllLabelsQuery
} from 'src/services/campaign-service'
import { useGetCampaignEmailsMutation } from 'src/services/unibox-service'
import {
  useGetLeadCampaignsByLabelMutation,
  useGetLeadsCampaignByWorkspaceAndStageMutation,
  useGetleadsCampaignsMutation
} from 'src/services/leads-service'
import {
  useUpdatePipelineMutation,
  useUpdatePipelineValueMutation,
  useDeletePipelineMutation,
  useUpdatePipelineStageMutation,
  useDeletePipelineStageMutation,
  useGetPipelineStagesByWorkspaceQuery
} from 'src/services/dealpipeline-crm-service'
import DancingDotsSpinner from 'src/components/dealPipeline/DancingDotSpinner'
import NoPipelineView from 'src/components/dealPipeline/NoPipelineView'
import HeaderWithPopoverUpdated from 'src/components/HeaderWithPopoverUpdated'
import { useGetMeAndWorkspaceQuery } from 'src/services/user-service'

const PipelineBoard = React.lazy(() => import('src/components/dealPipeline/PipelineBoard'))
const SlidingModal = React.lazy(() => import('src/components/dealPipeline/user-detail/SlidingModal'))
const AddNewDealModal = React.lazy(() => import('src/components/dealPipeline/AddNewDealModal'))
const EditColumnModal = React.lazy(() => import('src/components/dealPipeline/EditColumnModal'))
const ColumnManagementModal = React.lazy(() => import('src/components/dealPipeline/ColumnManagementModal'))
const DeleteStageModal = React.lazy(() => import('src/components/dealPipeline/DeleteStageModal'))

const DealPipeline = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [pipelineData, setPipelineData] = useState(null)
  const [dropdownAnchor, setDropdownAnchor] = useState(null)
  const [menuAnchor, setMenuAnchor] = useState({ anchor: null, columnId: null })
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [workspaceId, setWorkspaceId] = useState()
  const [userId, setUserId] = useState()
  const [selectedPipeline, setSelectedPipeline] = useState(null)

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editColumnData, setEditColumnData] = useState(null)
  const [isUserSlideModalOpen, setIsUserSlideModalOpen] = useState(false)
  const [selectedUserDeal, setSelectedUserDeal] = useState(null)
  const [dragDropPosition] = useState(null)
  const [columnVisibilityId, setColumnVisibilityId] = useState([])
  const [isAddNewDealModalOpen, setIsAddNewDealModalOpen] = useState(false)
  const [isCreatePipelineModalOpen, setIsCreatePipelineModalOpen] = useState(false)
  const [selectedUserColumnId, setSelectedUserColumnId] = useState(null)
  const [skipRefetch, setSkipRefetch] = useState(false)
  const [pipelineDeleted, setPipelineDeleted] = useState(false)
  const [deletedPipeline, setDeletedPipeline] = useState(null)
  const [loadingColumns, setLoadingColumns] = useState({})
  const [stageToDelete, setStageToDelete] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [selectedMigrationStageId, setSelectedMigrationStageId] = useState('')
  const { data } = useGetMeAndWorkspaceQuery()

  const [mainFilter, setMainFilter] = useState({
    sortBy: 'Name',
    order: 'Ascending'
  })

  const {
    data: labelsResponse,
    isLoading: isAllLabelsLoading,
    refetch: refetchLabels
  } = useGetAllLabelsQuery(workspaceId, { skip: !workspaceId })

  const { data: accountLevelStages = [] } = useGetPipelineStagesByWorkspaceQuery(workspaceId)
  const [deletePipelineStage] = useDeletePipelineStageMutation()

  const [getCampaignEmails] = useGetCampaignEmailsMutation()

  // const [updateLabelsWithData] = useUpdateLabelWithDataMutation()
  const [getleadsCampaigns] = useGetleadsCampaignsMutation()

  const [updatePipeline] = useUpdatePipelineMutation()
  const [updatePipelineValue] = useUpdatePipelineValueMutation()
  const [deletePipeline] = useDeletePipelineMutation()
  const [getLeadCampaignsByLabel] = useGetLeadCampaignsByLabelMutation()

  // const [getLeadCampaignsByPipeline] = useGetLeadCampaignsByPipelineMutation()
  const [getLeadsCampaignByWorkspaceAndStage] = useGetLeadsCampaignByWorkspaceAndStageMutation()
  const [updatePipelineStage] = useUpdatePipelineStageMutation()

  const workspace = useSelector((state) => state.workspace)
  const user = useSelector((state) => state.user)

  useEffect(() => {
    if (workspace && workspace._id) {
      setWorkspaceId(workspace._id)
    }
    if (user && user._id) {
      setUserId(user._id)
    }
  }, [workspace, user])

  useEffect(() => {
    // Prevent vertical scrolling on the entire document
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'

    return () => {
      // Clean up when component unmounts
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }
  }, [])

  useEffect(() => {
    // If skipRefetch is true, then reset it and skip fetching
    if (skipRefetch) {
      setSkipRefetch(false)
      return
    }

    if (!selectedPipeline) return
    if (isAllLabelsLoading) return

    setIsLoading(true)

    fetchPipelineDataCore()
      .then((data) => {
        if (dragDropPosition) {
          onDragEnd(dragDropPosition)
        }
        setIsLoading(false)
      })
      .catch((error) => {
        toast.error(`Failed to fetch pipeline data ${error.message}`)
        setIsLoading(false)
      })
  }, [selectedPipeline, labelsResponse])

  // Cleanup the state when the SlidingModal is unmounted
  useEffect(() => {
    if (!isUserSlideModalOpen) {
      setSelectedUserDeal(null)
    }
  }, [isUserSlideModalOpen])

  useEffect(() => {
    if (!pipelineData || !selectedPipeline) return
    if (selectedPipeline?.type !== 'custom') return
    if (selectedPipeline.totalValue !== 0) return

    const { columns } = pipelineData
    const totalValue = Object.values(columns)
      .filter((col) => col.visible !== false)
      .reduce((sum, column) => sum + (column.valuePerDeal || 0), 0)

    if (selectedPipeline.totalValue === totalValue) return

    // 1. Optimistically update local selectedPipeline
    setSelectedPipeline((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        totalValue
      }
    })

    // 2. Update in backend
    updatePipelineValue({
      id: selectedPipeline.id,
      data: {
        totalValue,
        userId,
        workspaceId
      }
    })

    // 3. Avoid triggering refetch
    setSkipRefetch(true)
  }, [pipelineData])

  useEffect(() => {
    if (!isEditModalOpen) {
      setEditColumnData(null)
    }
  }, [isEditModalOpen])

  const fetchPipelineDataCore = async () => {
    if (!selectedPipeline) return

    const isCustom = selectedPipeline.type === 'custom'
    const allColumnsMap = {}
    const columnPromises = []

    const stagesToUse = isCustom
      ? selectedPipeline.stages
      : labelsResponse?.labels?.filter((label) =>
        selectedPipeline.labels.some((pipelineLabel) => pipelineLabel._id === label._id)
      )

    if (!stagesToUse || stagesToUse.length === 0) {
      setPipelineData({ columns: {} })
      return
    }

    for (const stage of stagesToUse) {
      const stageId = isCustom ? stage.labelId?.toString() || stage._id?.toString() : stage._id.toString()
      const valuePerDeal = isCustom ? stage.valuePerDeal : stage.valuePerDeal
      const title = isCustom ? stage.title : stage.name
      const stageType = stage.type || 'default'
      const stageOrder = stage.order || 0

      allColumnsMap[stageId] = {
        id: stageId,
        title,
        visible: true,
        valuePerDeal: 0,
        defaultValuePerDeal: valuePerDeal,
        type: stageType,
        order: stageOrder,
        cards: []
      }

      columnPromises.push(
        (async () => {
          setLoadingColumns((prev) => ({ ...prev, [stageId]: true }))

          try {
            let users = []

            if (isCustom) {
              const customUsers = await getLeadsCampaignByWorkspaceAndStage({
                // pipelineId: selectedPipeline.id,
                workspaceId,
                pipelineStageId: stageId
              }).unwrap()

              users = customUsers.map((user) => ({
                id: user._id,
                name: `${user.firstName} ${user.lastName}`,
                title: user.title,
                email: user.email,
                date: format(parseISO(user.createdAt), 'd MMMM yyyy'),
                time: format(parseISO(user.createdAt), 'h:mm a'),
                avatar: user.avatar || 'https://via.placeholder.com/40',
                additionalData: user,
                label: stageId,
                isDealPipelineUser: user.addedFromDealPipeline,
                valuePerDeal: user.valuePerDeal || valuePerDeal,
                isUserHasOwnDealValue: user.valuePerDeal !== null && user.valuePerDeal !== undefined
              }))
            } else {
              const [emailsResponse, leadCampaignsResponse] = await Promise.all([
                getCampaignEmails({ label: stageId, workspaceId }).unwrap(),
                getLeadCampaignsByLabel({ labels: stageId, workspaceId }).unwrap()
              ])

              const leadIdsFromEmails = emailsResponse.docs.map((email) => email.lead_id)
              const leadIdsFromCampaigns = leadCampaignsResponse.map((lead) => lead._id)
              const combinedLeadIds = [...new Set([...leadIdsFromEmails, ...leadIdsFromCampaigns])]

              if (combinedLeadIds.length > 0) {
                const userDetailsResponse = await getleadsCampaigns({
                  leadIds: combinedLeadIds
                }).unwrap()

                users = userDetailsResponse.map((user) => ({
                  id: user._id,
                  name: `${user.firstName} ${user.lastName}`,
                  title: user.title,
                  email: user.email,
                  date: format(parseISO(user.createdAt), 'd MMMM yyyy'),
                  time: format(parseISO(user.createdAt), 'h:mm a'),
                  avatar: user.avatar || 'https://via.placeholder.com/40',
                  additionalData: user,
                  label: stageId,
                  isDealPipelineUser: user.addedFromDealPipeline,
                  valuePerDeal: user.valuePerDeal || valuePerDeal,
                  isUserHasOwnDealValue: user.valuePerDeal !== null && user.valuePerDeal !== undefined
                }))
              }
            }

            return { stageId, users }
          } catch (error) {
            toast.error(`Failed to fetch users for stage/label "${title}"`)
            return { stageId, users: [] }
          } finally {
            setLoadingColumns((prev) => ({ ...prev, [stageId]: false }))
          }
        })()
      )
    }

    const results = await Promise.allSettled(columnPromises)
    const successful = results.filter((r) => r.status === 'fulfilled').map((r) => r.value)

    for (const { stageId, users } of successful) {
      allColumnsMap[stageId].cards = users
      const totalValue = users.reduce(
        (sum, user) => sum + (user.valuePerDeal || allColumnsMap[stageId].defaultValuePerDeal),
        0
      )
      allColumnsMap[stageId].valuePerDeal = totalValue
    }

    setPipelineData({ columns: allColumnsMap })

    return { columns: allColumnsMap }
  }

  const disableRefetch = () => {
    setSkipRefetch(true)
  }

  const updateDealInPipelineData = ({ type, deal, oldColumnId, newColumnId, updatedValue }) => {
    setPipelineData((prev) => {
      if (!prev || !prev.columns) return prev

      const updatedColumns = { ...prev.columns }

      // 1. DELETE or MOVE (remove from old column)
      if ((type === 'delete' || type === 'move') && oldColumnId) {
        setIsUserSlideModalOpen(false)

        const oldColumn = updatedColumns[oldColumnId]
        oldColumn.cards = oldColumn?.cards?.filter((card) => card.id !== deal.id)
        oldColumn.valuePerDeal = oldColumn?.cards.reduce(
          (sum, card) => sum + (card.valuePerDeal || 0),
          0
        )
      }

      // 2. EDIT or VALUE CHANGE (update in place)
      if ((type === 'edit' || type === 'valueChange') && oldColumnId) {
        const column = updatedColumns[oldColumnId]
        const updatedCards = column.cards.map((card) =>
          card.id === deal.id
            ? {
                ...card,
                ...(type === 'edit' ? deal : {}), // full deal for edit
                ...(type === 'valueChange' ? { valuePerDeal: updatedValue } : {})
              }
            : card
        )

        column.cards = updatedCards
        column.valuePerDeal = updatedCards.reduce((sum, card) => sum + (card.valuePerDeal || 0), 0)
      }

      // 3. MOVE (add to new column)
      if (type === 'move' && newColumnId) {
        const newColumn = updatedColumns[newColumnId]

        // Update card value if user doesn't have custom value
        const updatedCards = newColumn?.cards?.map((card) => {
          if (!card.isUserHasOwnDealValue && newColumn.defaultValuePerDeal) {
            return {
              ...card,
              valuePerDeal: card.valuePerDeal || newColumn.defaultValuePerDeal
            }
          }
          return card
        })

        newColumn.cards = updatedCards

        // Recalculate the total column value
        newColumn.valuePerDeal = updatedCards.reduce(
          (sum, card) => sum + (card.valuePerDeal || 0),
          0
        )
      }

      // 4. ADD new deal to a column
      if (type === 'add' && newColumnId) {
        const newColumn = updatedColumns[newColumnId]
        newColumn.cards = [...newColumn.cards, deal]
        newColumn.valuePerDeal = newColumn.cards.reduce(
          (sum, card) => sum + (card.valuePerDeal || newColumn.defaultValuePerDeal || 0),
          0
        )
      }

      // 5. COLUMN VALUE CHANGE
      if (type === 'columnValueChange' && oldColumnId) {
        const column = updatedColumns[oldColumnId]
        column.defaultValuePerDeal = updatedValue

        column.cards = column.cards.map((card) => {
          return {
            ...card,
            valuePerDeal: !card.isUserHasOwnDealValue ? updatedValue : card.valuePerDeal
          }
        })

        column.valuePerDeal = column.cards.reduce(
          (sum, card) => sum + (card.valuePerDeal || column.defaultValuePerDeal || 0),
          0
        )
      }

      // 6. Recalculate total pipeline value
      const totalValue = Object.values(updatedColumns).reduce((sum, column) => {
        if (!column.visible) return sum

        const columnTotal = column.cards.reduce(
          (cardSum, card) => cardSum + (card.valuePerDeal || column.defaultValuePerDeal || 0),
          0
        )

        return sum + columnTotal
      }, 0)

      // optimistic update of the pipeline value
      updatePipelineValue({
        id: selectedPipeline.id,
        data: { totalValue, userId, workspaceId }
      })

      setSkipRefetch(true)

      return {
        ...prev,
        columns: updatedColumns
      }
    })
  }

  const handleDropdownClick = (event) => {
    setDropdownAnchor(event.currentTarget)
  }

  const handleDropdownClose = (option) => {
    // if (option) setSelectedOption(option);
    setDropdownAnchor(null)
  }

  const handleMenuOpen = (event, columnId) => {
    setMenuAnchor({ anchor: event.currentTarget, columnId })
  }

  const handleMenuClose = () => {
    setMenuAnchor({ anchor: null, columnId: null })
  }

  const updatePipelineData = async (data) => {
    if (selectedPipeline) {
      const {
        data: { pipeline }
      } = await updatePipeline({
        id: selectedPipeline.id,
        data: { labels: data.labels, values: data.values, userId, workspaceId }
      })

      return pipeline
    }
  }

  const handleUpatedLabelsForPipeline = async (updatedLabels, values) => {
    const pipeline = await updatePipelineData({ labels: updatedLabels, values })

    const updatedPipeline =
      pipeline && pipeline[0]
        ? {
            label: pipeline[0].title,
            totalValue: pipeline[0].totalValue,
            id: pipeline[0]._id,
            labels: pipeline[0].labels
          }
        : {}

    setSelectedPipeline(updatedPipeline)

    setPipelineData((prev) => {
      if (!prev || !prev.columns) return prev

      const updatedColumns = { ...prev.columns }

      Object.keys(updatedColumns).forEach((columnId) => {
        updatedColumns[columnId] = {
          ...updatedColumns[columnId],
          visible: updatedLabels.includes(columnId)
        }
      })

      return {
        ...prev,
        columns: updatedColumns
      }
    })

    setEditColumnData(null)
    setIsEditModalOpen(false)
    toast.success('Pipeline updated successfully')
  }

  const handleColumnVisibilityToggle = async (columnId) => {
    setColumnVisibilityId((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId)
      } else {
        return [...prev, columnId]
      }
    })

    setPipelineData((prev) => {
      return {
        ...prev,
        columns: {
          ...prev.columns,
          [columnId]: {
            ...prev.columns[columnId],
            visible: !prev.columns[columnId].visible
          }
        }
      }
    })
  }

  const handleRemoveColumn = async (columnId) => {
    const labelIds = Object.values(pipelineData.columns)
      .filter((column) => column.visible && column.id !== columnId)
      .map((column) => column.id)

    const values = Object.values(pipelineData.columns)
      .filter((column) => column.visible && column.id !== columnId)
      .map((column) => column.valuePerDeal)

    await updatePipelineData({ labels: labelIds, values })
  }

  const handleColumnManagementModalClose = () => {
    if (columnVisibilityId.length > 0) {
      setPipelineData((prev) => {
        const updatedColumns = { ...prev.columns }

        columnVisibilityId.forEach((columnId) => {
          if (updatedColumns[columnId]) {
            updatedColumns[columnId] = {
              ...updatedColumns[columnId],
              visible: !updatedColumns[columnId].visible
            }
          }
        })

        return {
          ...prev,
          columns: updatedColumns
        }
      })
    }

    setColumnVisibilityId([])

    setIsModalOpen(false)
  }

  const handleDeleteColumnAndMigrateLeads = async () => {
    const totalColumns = Object.keys(pipelineData?.columns).length
    if (totalColumns <= 1) {
      toast.error('You cannot delete the last stage in the pipeline')
      return
    }

    const totalDeals = pipelineData?.columns[menuAnchor.columnId].cards?.length || 0

    setStageToDelete({
      stageId: menuAnchor.columnId,
      title: menuAnchor.columnTitle,
      pipelineId: selectedPipeline?.id,
      totalDeals
    })
    setIsDeleteModalOpen(true)
    handleMenuClose()
  }

  const handleConfirmDelete = async () => {
    try {
      await deletePipelineStage({
        pipelineId: stageToDelete.pipelineId,
        stageId: stageToDelete.stageId,
        targetStageId: selectedMigrationStageId,
        workspaceId
      }).unwrap()

      toast.success('Stage deleted and deals migrated')
      setIsDeleteModalOpen(false)
      setStageToDelete(null)
      setSelectedMigrationStageId('')

      setSelectedPipeline((prev) => {
        if (!prev) return prev
        const updatedStages = prev.stages.filter((stage) => stage.labelId !== stageToDelete.stageId)

        return {
          ...prev,
          stages: updatedStages
        }
      })

      setPipelineData((prev) => {
        if (!prev || !prev.columns) return prev

        const updatedColumns = { ...prev.columns }
        delete updatedColumns[stageToDelete.stageId]

        return {
          ...prev,
          columns: updatedColumns
        }
      })
    } catch (err) {
      toast.error('Failed to delete stage')
    }
  }

  const handleDeleteColumnLegacy = async () => {
    const columnId = menuAnchor.columnId

    setColumnVisibilityId((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((id) => id !== columnId)
      } else {
        return [...prev, columnId]
      }
    })

    setPipelineData((prev) => {
      return {
        ...prev,
        columns: {
          ...prev.columns,
          [columnId]: {
            ...prev.columns[columnId],
            visible: !prev.columns[columnId].visible
          }
        }
      }
    })

    await handleRemoveColumn(columnId)
    setEditColumnData(null)
    setIsEditModalOpen(false)
    handleMenuClose()
    toast.success('Column deleted successfully')
  }

  const onDragEnd = (result) => {
    // Check if there's a valid source and destination
    if (!result || !result.source || !result.destination) return

    const { source, destination } = result

    // Exit if the source and destination are the same
    if (source.index === destination.index) return

    // Ensure pipelineData and columns are available
    if (!pipelineData || !pipelineData.columns) {
      toast.error('Failed to reorder columns')
      return
    }

    // Convert columns object to an array for reordering
    const columnsArray = Object.entries(pipelineData.columns)

    // Move the dragged column
    const [movedColumn] = columnsArray.splice(source.index, 1)
    columnsArray.splice(destination.index, 0, movedColumn)

    // Convert back to an object
    const reorderedColumns = Object.fromEntries(columnsArray)

    // Update the pipelineData state
    setPipelineData((prev) => ({
      ...prev,
      columns: reorderedColumns
    }))
  }

  const handleEditColumn = (columnId) => {
    setEditColumnData({
      columnId,
      ...pipelineData.columns[columnId]
    })

    setIsEditModalOpen(true)
  }

  const handleSaveColumnChanges = async (updatedColumn) => {
    const { columnId, title, valuePerDeal } = updatedColumn

    setEditColumnData(null)

    await updatePipelineStage({
      pipelineId: selectedPipeline.id,
      stageId: columnId,
      title,
      valuePerDeal
    })

    // await updateLabelsWithData({
    //   labelId: columnId,
    //   data: {
    //     name: title,
    //     valuePerDeal
    //   }
    // })

    // const labelIds = Object.values(pipelineData.columns)
    //   .filter((column) => column.visible)
    //   .map((column) => column.id)

    // const values = Object.values(pipelineData.columns)
    //   .filter((column) => column.visible)
    //   .map((column) => column.valuePerDeal)

    setPipelineData((prev) => {
      return {
        ...prev,
        columns: {
          ...prev.columns,
          [columnId]: {
            ...prev.columns[columnId],
            title,
            valuePerDeal
          }
        }
      }
    })

    // await updatePipelineData({ labels: labelIds, values })
    setIsEditModalOpen(false)
    handleMenuClose()

    toast.success('Stage updated successfully')
    // optimistic update of the pipeline value
    updateDealInPipelineData({
      type: 'columnValueChange',
      oldColumnId: columnId,
      updatedValue: valuePerDeal
    })
  }

  const handlePipelineHeaderAction = (action) => {
    setMainFilter(action)
  }

  const handleLabelChange = (isLabelChanged, hideModal = false) => {
    if (isLabelChanged) {
      if (hideModal) {
        setIsUserSlideModalOpen(false)
      }

      refetchLabels()
        .unwrap()
        .then((newLabels) => {
          const updatedPipeline = {
            ...selectedPipeline
          }
          setSelectedPipeline(updatedPipeline) // Triggers the useEffect
        })
        .catch((error) => {
          toast.error('Failed to update labels. Please try again.', error)
        })
    }
  }

  const handleDeletePipeline = async (pipelineId) => {
    try {
      const deletedPipeline = await deletePipeline(pipelineId).unwrap()
      if (deletedPipeline) {
        toast.success('Pipeline deleted successfully')
        setDeletedPipeline(deletedPipeline?.pipeline)
        setPipelineDeleted(true)
        if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
          window.localStorage.setItem('lastSelectedPipelineId', pipelineId)
        }
      }
    } catch (error) {
      toast.error('Failed to delete pipeline. Please try again.')
    }
  }

  const handleColumnOrderChange = async (newOrder) => {
    if (!pipelineData?.columns || !selectedPipeline?.id) return

    setPipelineData(prev => {
      const updatedColumns = { ...prev.columns }
      newOrder.forEach((columnId, index) => {
        if (updatedColumns[columnId]) {
          updatedColumns[columnId] = {
            ...updatedColumns[columnId],
            order: index
          }
        }
      })

      return {
        ...prev,
        columns: updatedColumns
      }
    })

    setSelectedPipeline(prev => {
      if (!prev?.stages) return prev

      const reorderedStages = prev.stages.map(stage => ({ ...stage }))

      newOrder.forEach((columnId, index) => {
        const stage = reorderedStages.find(
          s => (s.labelId || s._id)?.toString() === columnId
        )
        if (stage) stage.order = index
      })

      return {
        ...prev,
        stages: reorderedStages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      }
    })
  }

  const sortColumns = (columns, mainFilter) => {
    return Object.entries(columns)
      .filter(([, column]) => column.visible) // Filter out only visible columns
      .sort(([idA, colA], [idB, colB]) => {
        if (mainFilter.sortBy === 'Name') {
          const nameA = colA.title.toLowerCase()
          const nameB = colB.title.toLowerCase()
          return mainFilter.order === 'Ascending'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA)
        } else if (mainFilter.sortBy === 'Last Activity') {
          const lastActivityA = Math.max(
            ...colA.cards.map((card) => new Date(card.additionalData.updatedAt || 0).getTime())
          )
          const lastActivityB = Math.max(
            ...colB.cards.map((card) => new Date(card.additionalData.updatedAt || 0).getTime())
          )
          return mainFilter.order === 'Ascending'
            ? lastActivityA - lastActivityB
            : lastActivityB - lastActivityA
        } else if (mainFilter.sortBy === 'Date Created') {
          const dateCreatedA = Math.min(
            ...colA.cards.map((card) => new Date(card.additionalData.createdAt || 0).getTime())
          )
          const dateCreatedB = Math.min(
            ...colB.cards.map((card) => new Date(card.additionalData.createdAt || 0).getTime())
          )
          return mainFilter.order === 'Ascending'
            ? dateCreatedA - dateCreatedB
            : dateCreatedB - dateCreatedA
        }
        return 0 // Default to no sorting
      })
      .reduce((acc, [columnId, column]) => {
        acc[columnId] = column
        return acc
      }, {})
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: '100%',
          height: '100%',
          overflowY: 'scroll',
          '&::-webkit-scrollbar': {
            display: 'none' // Hide scrollbar for WebKit browsers
          }
        }}
      >
        <Box
          sx={{
            width: '96%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: 'row',
              pb: 2
            }}
          >
            <HeaderWithPopoverUpdated
              title='Deal Pipeline'
              onWorkspaceChange={() => { }}
              selectedTimeline={data?.workspace}
            />
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                // mb: { xs: 2, md: 4 },
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%',
                justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                // borderBottom: "1px solid #E4E4E5",
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  width: { xs: '100%', sm: 'auto' },
                  flexDirection: { xs: 'row', sm: 'row' },
                  alignItems: 'center'
                }}
              >
                <Box sx={{ display: 'flex' }}>
                  {workspaceId && userId && (
                    <PipelineSelector
                      onDropdownClick={handleDropdownClick}
                      dropdownAnchor={dropdownAnchor}
                      onDropdownClose={handleDropdownClose}
                      setSelectedPipeline={setSelectedPipeline}
                      workspaceId={workspaceId}
                      userId={userId}
                      openCreatePipelineModal={isCreatePipelineModalOpen}
                      closeCreatePipelineModal={() => setIsCreatePipelineModalOpen(false)}
                      pipelineDeleted={pipelineDeleted} // boolean to tell if the pipeline is deleted
                      deletedPipeline={deletedPipeline} // actual pipeline that was deleted
                      onResetPipelineDeleted={() => {
                        setPipelineDeleted(false)
                      }} // resets the trigger
                      labels={labelsResponse?.labels}
                      setPipelineData={setPipelineData}
                    />
                  )}
                </Box>
                <PipelineHeaderActions
                  onDropdownClick={handleDropdownClick}
                  onModalOpen={() => setIsModalOpen(true)}
                  onAddNewDealModalOpen={() => {
                    // Clear query parameters
                    const url = new URL(window.location)
                    url.search = '' // Remove all query parameters
                    window.history.replaceState({}, document.title, url)

                    setIsAddNewDealModalOpen(true)
                  }}
                  onPipelineHeaderAction={handlePipelineHeaderAction}
                  SelectedPipeline={selectedPipeline}
                  onDeletePipeline={(id) => handleDeletePipeline(id)}
                />
              </Box>
            </Box>

          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              width: '100%',
              height: '100%'
            }}
          >
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
                // alignItems: "center",
              }}
            >
              {/* <HeaderWithPopover
            title="Deal Pipeline"
            onWorkspaceChange={() => {}}
            selectedTimeline={data?.workspace}
          /> */}

              <Box
                sx={{
                  height: 'calc(100vh - 105px)',
                  width: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden'
                }}
              >
                {!pipelineData && (isAllLabelsLoading || isLoading) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      display: 'flex',
                      pointerEvents: 'none'
                    }}
                  >
                    <DancingDotsSpinner />
                  </Box>
                )}

                <>
                  {/* Header */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                      paddingY: '20px'
                    }}
                  >
                    {/* <Box sx={{ display: "flex" }}>
                    {workspaceId && userId && (
                      <PipelineSelector
                        onDropdownClick={handleDropdownClick}
                        dropdownAnchor={dropdownAnchor}
                        onDropdownClose={handleDropdownClose}
                        setSelectedPipeline={setSelectedPipeline}
                        workspaceId={workspaceId}
                        userId={userId}
                      />
                    )}
                  </Box> */}
                    {/* <PipelineHeaderActions
                    onDropdownClick={handleDropdownClick}
                    onModalOpen={() => setIsModalOpen(true)}
                    onAddNewDealModalOpen={() => setIsAddNewDealModalOpen(true)}
                    onPipelineHeaderAction={handlePipelineHeaderAction}
                  /> */}
                  </Box>

                  {/* from here the Actual content starts */}
                  <>
                    {pipelineData
                      ? (
                        <>
                          <Suspense fallback={<DancingDotsSpinner />}>
                            <ColumnManagementModal
                              isOpen={isModalOpen}
                              onClose={handleColumnManagementModalClose}
                              pipelineData={pipelineData}
                              setPipelineData={setPipelineData}
                              labels={labelsResponse.labels}
                              onColumnVisibilityToggle={handleColumnVisibilityToggle}
                              onDeleteColumn={handleDeleteColumnLegacy}
                              onMenuOpen={handleMenuOpen}
                              onMenuClose={handleMenuClose}
                              onDragEnd={onDragEnd}
                              refetchLabels={refetchLabels}
                              updatedLabelsForPipeline={handleUpatedLabelsForPipeline}
                              disableRefetch={disableRefetch}
                            />
                          </Suspense>
                        </>
                        )
                      : (
                        <>
                          {!selectedPipeline && (
                            <NoPipelineView onCreatePipeline={() => setIsCreatePipelineModalOpen(true)} />
                          )}
                        </>
                        )}

                    {/* Deal Pipeline Grid */}
                    {pipelineData && (
                      <Suspense fallback={<DancingDotsSpinner />}>
                        <PipelineBoard
                          columns={sortColumns(pipelineData.columns, mainFilter)}
                          loadingColumns={loadingColumns}
                          onMenuOpen={handleMenuOpen}
                          onUserCardClick={(card, columnId) => {
                            setSelectedUserDeal(card)
                            setSelectedUserColumnId(columnId)
                            setIsUserSlideModalOpen(true)
                          }}
                          onAddDeal={() => setIsAddNewDealModalOpen(true)}
                          updateDealInPipelineData={updateDealInPipelineData}
                          selectedPipeline={selectedPipeline}
                          onColumnOrderChange={handleColumnOrderChange}
                        />
                      </Suspense>
                    )}
                  </>

                  {/* Column Menu */}
                  <Menu
                    anchorEl={menuAnchor.anchor}
                    open={Boolean(menuAnchor.anchor)}
                    onClose={handleMenuClose}
                  >
                    {/* <MenuItem
                  onClick={() => {
                    handleColumnVisibilityToggle(menuAnchor.columnId);
                    handleRemoveColumn(menuAnchor.columnId);
                    handleMenuClose();
                    setColumnVisibilityId([]);
                  }}
                >
                  Hide column
                </MenuItem> */}
                    <MenuItem
                      onClick={() => {
                        handleEditColumn(menuAnchor.columnId)
                      }}
                    >
                      Edit column
                    </MenuItem>
                    {editColumnData && (
                      <Suspense fallback={<DancingDotsSpinner />}>
                        <EditColumnModal
                          isOpen={isEditModalOpen}
                          onClose={() => {
                            setIsEditModalOpen(false)
                            setEditColumnData(null)
                            handleMenuClose()
                          }}
                          columnData={editColumnData}
                          onSave={handleSaveColumnChanges}
                        />
                      </Suspense>
                    )}
                    <MenuItem
                      onClick={handleDeleteColumnAndMigrateLeads}
                      sx={{ color: 'red', display: 'flex', alignItems: 'center' }}
                    >
                      <DeleteIcon sx={{ marginRight: '8px' }} />
                      Delete column
                    </MenuItem>
                  </Menu>
                </>
              </Box>
            </Box>
          </Box>

          {selectedUserDeal && (
            <Suspense fallback={<DancingDotsSpinner />}>
              <SlidingModal
                isOpen={isUserSlideModalOpen}
                onClose={() => setIsUserSlideModalOpen(false)}
                user={selectedUserDeal}
                workspaceId={workspaceId}
                labels={labelsResponse.labels}
                loggedInUser={userId}
                onUpdateComplete={handleLabelChange}
                updateDealInPipelineData={updateDealInPipelineData}
                selectedUserColumnId={selectedUserColumnId}
              />
            </Suspense>
          )}

          {isDeleteModalOpen && (
            <Suspense fallback={<DancingDotsSpinner />}>
              <DeleteStageModal
                open={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                stageName={stageToDelete?.stageName || ''}
                stages={accountLevelStages.filter((s) => s._id !== stageToDelete?.stageId)}
                selectedStageId={selectedMigrationStageId}
                setSelectedStageId={setSelectedMigrationStageId}
                totalDeals={stageToDelete?.totalDeals || 0}
              />
            </Suspense>
          )}

          {isAddNewDealModalOpen && (
            <Suspense fallback={<DancingDotsSpinner />}>
              <AddNewDealModal
                isOpen={isAddNewDealModalOpen}
                onClose={() => setIsAddNewDealModalOpen(false)}
                onCreateDeal={(deal) => {
                  setIsAddNewDealModalOpen(false)
                }}
                workspaceId={workspaceId}
                updateDealInPipelineData={updateDealInPipelineData}
                labels={labelsResponse.labels}
                loggedInUser={userId}
                selectedPipeline={selectedPipeline}
              />
            </Suspense>
          )}
        </Box>
      </Box>
    </>
  )
}

export default DealPipeline
